import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { AUDIENCES, getSportsForAudience } from "@/constants/categories";
import { SPORT_ICONS, sportColor } from "@/constants/categoryIcons";
import { Trophy } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { wallpaperStyle, WALLPAPER_TOP } from "@/lib/wallpaper";

const SportCard = ({ sport, audience, index }) => {
  const navigate = useNavigate();
  const { tt } = useLang();
  const Icon = SPORT_ICONS[sport.slug] || Trophy;
  const color = sportColor(index);
  return (
    <button
      data-testid="sport-card"
      onClick={() => navigate(`/s/${audience}/athlitikoi?sport=${sport.slug}`)}
      className="group block w-full text-left rounded-2xl bg-white shadow-md ring-1 ring-black/5 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200"
    >
      <div className="relative">
        <div className="aspect-square overflow-hidden">
          <img src={sport.image} alt={tt(sport)} loading="eager" decoding="sync" className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300" />
        </div>
        <div className="absolute left-2 bottom-2 z-10 h-9 w-9 rounded-full bg-white shadow-lg ring-1 ring-black/5 grid place-items-center">
          <Icon className={color} size={17} strokeWidth={2.2} />
        </div>
      </div>
      <div className="px-2.5 py-1.5 min-h-[2.6rem] flex items-center">
        <p className="text-slate-900 font-display font-bold text-[11px] sm:text-[13px] leading-tight line-clamp-2">{tt(sport)}</p>
      </div>
    </button>
  );
};

const LangPill = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-full bg-white/70 p-0.5 text-xs font-bold shadow-sm" data-testid="page-lang-toggle">
      <button onClick={() => setLang("el")} className={`px-2.5 py-1 rounded-full transition-colors ${lang === "el" ? "bg-primary text-white" : "text-slate-700"}`}>ΕΛ</button>
      <button onClick={() => setLang("en")} className={`px-2.5 py-1 rounded-full transition-colors ${lang === "en" ? "bg-primary text-white" : "text-slate-700"}`}>EN</button>
    </div>
  );
};

const SportsPage = () => {
  const { audience } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const a = AUDIENCES[audience];
  if (!a) {
    navigate("/");
    return null;
  }
  const sports = getSportsForAudience(audience);

  return (
    <div className="relative min-h-screen" style={wallpaperStyle(audience)}>
      <header className="absolute top-0 inset-x-0 z-20">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between text-slate-900">
          <button onClick={() => navigate(`/c/${audience}`)} data-testid="sports-back-button" className="h-10 w-10 grid place-items-center rounded-full bg-white/60 hover:bg-white/90 transition-colors">
            <ArrowLeft size={22} />
          </button>
          <LangPill />
        </div>
      </header>

      <main className="relative max-w-6xl mx-auto px-4 pb-6" style={WALLPAPER_TOP}>
        <h1 className="mb-5 text-center font-display text-xl sm:text-2xl font-extrabold text-[#0B2454]" data-testid="sports-title">{lang === "en" ? "Sports" : "Αθλήματα"}</h1>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5 sm:gap-6 lg:gap-8" data-testid="sports-grid">
          {sports.map((sp, i) => (
            <SportCard key={sp.slug} sport={sp} audience={audience} index={i} />
          ))}
        </div>
      </main>
    </div>
  );
};

export default SportsPage;
